import { Badge, Group, Stack, Text } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import type { AssessmentItem } from '../types'

interface CurriculumBreadcrumbProps {
  item: AssessmentItem
}

/**
 * Renders the backend-provided breadcrumb as a Lv1 badge + chevron-separated
 * trail, with the lv4 description and code below. Mirrors the prototype's
 * CurriculumItemRow.
 */
export function CurriculumBreadcrumb({ item }: CurriculumBreadcrumbProps) {
  const color = item.curriculumKind === 'content' ? 'green' : 'blue'
  const trailParts = item.breadcrumb
    .split('>')
    .map(p => p.trim())
    .filter(Boolean)
  // First part = lv1 (badge); last part = item name (already shown below).
  const middle = trailParts.slice(1, Math.max(1, trailParts.length - 1))

  return (
    <Stack spacing={4}>
      <Group spacing={4} align="center" noWrap={false}>
        <Badge size="md" color={color} variant="light" style={{ textTransform: 'uppercase' }}>
          {item.lv1Name || trailParts[0]}
        </Badge>
        {middle.map((part, idx) => {
          const isLast = idx === middle.length - 1
          return (
            <Group key={idx} spacing={4} align="center" noWrap>
              <IconChevronRight size={14} color="var(--mantine-color-gray-6)" />
              <Text size="xs" color={isLast ? undefined : 'dimmed'} weight={isLast ? 500 : undefined}>
                {part}
              </Text>
            </Group>
          )
        })}
      </Group>
      <Text size="sm">
        {item.originalCodes ? (
          <>
            <Text span weight={700}>
              {item.originalCodes}
            </Text>
            {' - '}
            {item.name}
          </>
        ) : (
          item.name
        )}
      </Text>
    </Stack>
  )
}
